import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as socketService from '../services/socket';
import BlurredThumbnail from './BlurredThumbnail';

interface AudioPlayerProps {
  videoId: string;
  roomId: string;
  preloaded?: boolean;
  roundStartTime?: number;
}

const ROUND_SECONDS = 60;
// Only seek into the clip when we are clearly late (a rejoin), not for normal audio latency.
const LATE_JOIN_THRESHOLD_SECONDS = 3;

// Custom hook for persisting volume
const usePersistedVolume = (defaultVolume = 0.7) => {
  const STORAGE_KEY = 'songle-player-volume';
  
  // Get initial volume from localStorage
  const getStoredVolume = () => {
    try {
      const storedValue = localStorage.getItem(STORAGE_KEY);
      return storedValue ? parseFloat(storedValue) : defaultVolume;
    } catch (err) {
      console.error('Error accessing localStorage:', err);
      return defaultVolume;
    }
  };

  const [volume, setVolumeState] = useState<number>(getStoredVolume);

  // Update volume and persist to localStorage
  const setVolume = useCallback((newVolume: number) => {
    setVolumeState(newVolume);
    try {
      localStorage.setItem(STORAGE_KEY, newVolume.toString());
    } catch (err) {
      console.error('Error saving volume to localStorage:', err);
    }
  }, []);

  return [volume, setVolume] as const;
};

const AudioPlayer: React.FC<AudioPlayerProps> = ({ videoId, roomId, roundStartTime }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(ROUND_SECONDS);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [hasEnded, setHasEnded] = useState<boolean>(false);
  const [volume, setVolume] = usePersistedVolume();
  const audioRef = useRef<HTMLAudioElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // Seconds into the clip we should start at (non-zero only when (re)joining mid-round).
  const startOffsetRef = useRef<number>(0);

  const computeStartOffset = (): number => {
    if (!roundStartTime) return 0;
    const elapsed = (Date.now() - roundStartTime) / 1000;
    if (!Number.isFinite(elapsed) || elapsed < LATE_JOIN_THRESHOLD_SECONDS) return 0;
    return Math.min(ROUND_SECONDS - 1, Math.floor(elapsed));
  };

  // Reset timer and clear intervals when component unmounts or videoId changes
  const resetTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    // Reset states when videoId changes
    setIsLoading(true);
    setAudioUrl(null);
    setTimeRemaining(60);
    setIsPlaying(false);
    setHasEnded(false);
    resetTimer();

    if (!videoId || !roomId) return;
    
    console.log(`AudioPlayer: Requesting audio for ${videoId} in room ${roomId}`);
    
    // Request audio stream from server (only once)
    socketService.requestAudioStream(roomId, videoId);
    
    // Set up listeners
    const handleStreamStarting = ({ videoId: startingVideoId }: { videoId: string }) => {
      if (startingVideoId === videoId) {
        console.log(`Audio preparation starting for video ${videoId}`);
        setIsLoading(true);
        setAudioUrl(null);
        setTimeRemaining(60);
        setIsPlaying(false);
        resetTimer();
      }
    };

    const handleAudioReady = ({ videoId: readyVideoId, audioUrl: url }: { videoId: string, audioUrl: string }) => {
      if (readyVideoId === videoId) {
        console.log(`Audio ready for video ${videoId}: ${url}`);
        startOffsetRef.current = computeStartOffset();
        if (startOffsetRef.current > 0) {
          console.log(`Joined mid-round; starting playback at ${startOffsetRef.current}s`);
        }
        setAudioUrl(url);
        setIsLoading(false);
      }
    };

    const handleStreamError = ({ videoId: errorVideoId, error }: { videoId: string, error: string }) => {
      if (errorVideoId === videoId) {
        console.error(`Error in audio for video ${videoId}:`, error);
        setIsLoading(false);
      }
    };

    const handleError = (error: any) => {
      console.error('Socket error:', error);
      setIsLoading(false);
    };

    socketService.onAudioStreamStarting(handleStreamStarting);
    socketService.onAudioReady(handleAudioReady);
    socketService.onAudioStreamError(handleStreamError);
    socketService.onError(handleError);
    
    // Use imported socket instance for cleanup
    const socketInstance = socketService.default;
    const audioEl = audioRef.current;

    return () => {
      // Clean up audio element
      if (audioEl) {
        audioEl.pause();
        audioEl.src = '';
      }
      
      // Remove event listeners
      socketInstance.off('audioReady', handleAudioReady);
      socketInstance.off('audioStreamStarting', handleStreamStarting);
      socketInstance.off('audioStreamError', handleStreamError);
      socketInstance.off('error', handleError);
      
      resetTimer();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, roomId]);

  // Effect to play audio when URL is available
  useEffect(() => {
    if (audioUrl && audioRef.current) {
      console.log('Audio URL available, attempting to play:', audioUrl);
      
      // Reset timer state before starting new playback
      resetTimer();
      setTimeRemaining(Math.max(0, ROUND_SECONDS - startOffsetRef.current));
      
      const playAudio = async () => {
        try {
          if (startOffsetRef.current > 0 && audioRef.current) {
            audioRef.current.currentTime = startOffsetRef.current;
          }
          await audioRef.current?.play();
          console.log('Audio playback started successfully');
          setIsPlaying(true);
          
          // Start countdown timer
          timerRef.current = setInterval(() => {
            setTimeRemaining(prev => {
              if (prev <= 1) {
                // Clear the interval when time is up
                resetTimer();
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        } catch (err) {
          console.error('Error playing audio:', err);
          setIsPlaying(false);
        }
      };
      
      playAudio();
    } else {
      console.log('Audio URL not available or audio ref not ready');
      setIsPlaying(false);
    }
    
    return () => {
      resetTimer();
    };
  }, [audioUrl, videoId]);

  // Apply volume whenever slider changes or a new audio source mounts.
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      console.log(`Setting audio volume to ${volume}`);
    }
  }, [volume, audioUrl]);

  // Handle audio events
  const handlePlay = () => {
    console.log('Audio play event triggered');
    setIsPlaying(true);
  };

  const handlePause = () => {
    console.log('Audio pause event triggered');
    setIsPlaying(false);
  };

  const handleEnded = () => {
    console.log('Audio ended event triggered');
    setIsPlaying(false);
    setHasEnded(true);
  };

  // Handle volume change
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
  };

  const volumePercent = Math.round(volume * 100);

  return (
    <div className="audio-player">
      {isLoading && (
        <div className="audio-loading" role="status" aria-live="polite">
          <span className="spinner" />
          <span>Loading audio…</span>
        </div>
      )}
      {audioUrl && (
        <>
          <audio 
            ref={audioRef}
            src={audioUrl} 
            controls={false} 
            autoPlay 
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handleEnded}
            onLoadedMetadata={() => {
              // Some browsers ignore seeks issued before metadata is available; re-apply here.
              const el = audioRef.current;
              const offset = startOffsetRef.current;
              if (el && offset > 0 && el.currentTime < offset - 1) {
                el.currentTime = offset;
              }
            }}
            onError={(e) => {
              console.error('Audio error:', e);
              setIsPlaying(false);
            }}
          />
          {/* Keep the thumbnail mounted for the whole round so the de-blur progress isn't lost. */}
          <div className="now-playing">
            <div className="thumbnail-container">
              <BlurredThumbnail videoId={videoId} roomId={roomId} duration={60} isPlaying={isPlaying} />
            </div>
            {isPlaying ? (
              <div className="now-playing-badge" aria-hidden="true">
                <span className="equalizer">
                  <span />
                  <span />
                  <span />
                  <span />
                </span>
                Now playing
              </div>
            ) : hasEnded ? (
              <div className="now-playing-badge now-playing-badge--ended" aria-hidden="true">
                Round over
              </div>
            ) : (
              <div className="now-playing-badge" aria-hidden="true">
                <span className="spinner spinner--sm" />
                Starting…
              </div>
            )}
          </div>

          {isPlaying && (
            <>
              <div className={`timer ${timeRemaining < 10 ? 'time-low' : ''}`}>
                <div className="timer-row">
                  <span className="timer-label">Time left</span>
                  <span className="timer-value">{timeRemaining}s</span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${(timeRemaining / 60) * 100}%` }}
                  ></div>
                </div>
              </div>

              <div className="volume-control">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="18" 
                  height="18" 
                  fill="currentColor" 
                  className="volume-icon" 
                  viewBox="0 0 16 16"
                  aria-hidden="true"
                >
                  {volume > 0.66 && (
                    <path d="M11.536 14.01A8.473 8.473 0 0 0 14.026 8a8.473 8.473 0 0 0-2.49-6.01l-.708.707A7.476 7.476 0 0 1 13.025 8c0 2.071-.84 3.946-2.197 5.303l.708.707z"/>
                  )}
                  {volume > 0.33 && (
                    <path d="M10.121 12.596A6.48 6.48 0 0 0 12.025 8a6.48 6.48 0 0 0-1.904-4.596l-.707.707A5.483 5.483 0 0 1 11.025 8a5.483 5.483 0 0 1-1.61 3.89l.706.706z"/>
                  )}
                  <path d="M8.707 11.182A4.486 4.486 0 0 0 10.025 8a4.486 4.486 0 0 0-1.318-3.182L8 5.525A3.489 3.489 0 0 1 9.025 8 3.49 3.49 0 0 1 8 10.475l.707.707zM6.717 3.55A.5.5 0 0 1 7 4v8a.5.5 0 0 1-.812.39L3.825 10.5H1.5A.5.5 0 0 1 1 10V6a.5.5 0 0 1 .5-.5h2.325l2.363-1.89a.5.5 0 0 1 .529-.06z"/>
                </svg>
                <input
                  id="volume-slider"
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={handleVolumeChange}
                  className="range volume-slider"
                  aria-label="Volume"
                  style={{ ['--pct' as string]: `${volumePercent}%` }}
                />
                <span className="volume-value">{volumePercent}%</span>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AudioPlayer; 